import React from 'react';
import { ViewMore } from './ViewMoreLink.style';
import { LandingPageTypes } from '../../common/helpers/domain/landingPages';
import homeResource from '../../locales/th/home.json';
import {
  generateComboLandingPageUrl,
  generatePostMainCategoryLandingPageUrl,
  generatePostSubCategoryLandingPageUrl,
} from '../../common/helpers/routing/landingPageUrlGenerator';
import {
  PostMainCategory,
  PostSubcategory,
} from '../../common/helpers/domain/postCategories';
import { PropertyType } from '../../common/helpers/domain/propertyTypes';
import { ArrowRight } from '../global/icons/arrowRight';

interface ViewMoreLinkProps {
  landingPageType: LandingPageTypes;
  onClick?: (targetLandingPage: LandingPageTypes) => void;
}

const { viewMoreLinkLabel } = homeResource;
const viewMoreLinkConfig: Record<string, { label: string; link: string }> = {
  [LandingPageTypes.ReviewPage]: {
    label: viewMoreLinkLabel.review,
    link: generatePostSubCategoryLandingPageUrl(PostSubcategory.Review),
  },
  [LandingPageTypes.PreviewPage]: {
    label: viewMoreLinkLabel.preview,
    link: generatePostSubCategoryLandingPageUrl(PostSubcategory.Preview),
  },
  [LandingPageTypes.PointOfViewPage]: {
    label: viewMoreLinkLabel.pov,
    link: generatePostSubCategoryLandingPageUrl(PostSubcategory.PointOfView),
  },
  [LandingPageTypes.NewsPage]: {
    label: viewMoreLinkLabel.news,
    link: generatePostMainCategoryLandingPageUrl(PostMainCategory.News),
  },
  [LandingPageTypes.GuidesPage]: {
    label: viewMoreLinkLabel.guides,
    link: generatePostMainCategoryLandingPageUrl(PostMainCategory.Guides),
  },
  [LandingPageTypes.DecorsPage]: {
    label: viewMoreLinkLabel.decors,
    link: generatePostMainCategoryLandingPageUrl(PostMainCategory.Decor),
  },
  [LandingPageTypes.ReviewCondoPage]: {
    label: viewMoreLinkLabel.reviewCondo,
    link: generateComboLandingPageUrl(
      PostSubcategory.Review,
      PropertyType.Condominium,
    ),
  },
  [LandingPageTypes.ReviewHousePage]: {
    label: viewMoreLinkLabel.reviewHouse,
    link: generateComboLandingPageUrl(
      PostSubcategory.Review,
      PropertyType.House,
    ),
  },
  [LandingPageTypes.ReviewTownhomePage]: {
    label: viewMoreLinkLabel.reviewTownhome,
    link: generateComboLandingPageUrl(
      PostSubcategory.Review,
      PropertyType.Townhome,
    ),
  },
  [LandingPageTypes.PreviewCondoPage]: {
    label: viewMoreLinkLabel.previewCondo,
    link: generateComboLandingPageUrl(
      PostSubcategory.Preview,
      PropertyType.Condominium,
    ),
  },
  [LandingPageTypes.PreviewHousePage]: {
    label: viewMoreLinkLabel.previewHouse,
    link: generateComboLandingPageUrl(
      PostSubcategory.Preview,
      PropertyType.House,
    ),
  },
  [LandingPageTypes.PreviewTownhomePage]: {
    label: viewMoreLinkLabel.previewTownhome,
    link: generateComboLandingPageUrl(
      PostSubcategory.Preview,
      PropertyType.Townhome,
    ),
  },
  [LandingPageTypes.PropertyNewsPage]: {
    label: viewMoreLinkLabel.newsPropertyPage,
    link: generatePostSubCategoryLandingPageUrl(PostSubcategory.PropertyNews),
  },
  [LandingPageTypes.GeneralNewsPage]: {
    label: viewMoreLinkLabel.newsGeneralPage,
    link: generatePostSubCategoryLandingPageUrl(PostSubcategory.GeneralNews),
  },
  [LandingPageTypes.BuyAndSellPage]: {
    label: viewMoreLinkLabel.guidesBuyAndSellPage,
    link: generatePostSubCategoryLandingPageUrl(PostSubcategory.BuyAndSell),
  },
  [LandingPageTypes.FinancePage]: {
    label: viewMoreLinkLabel.guidesFinancialPage,
    link: generatePostSubCategoryLandingPageUrl(PostSubcategory.Finance),
  },
  [LandingPageTypes.InvestmentPage]: {
    label: viewMoreLinkLabel.guidesInvestmentPage,
    link: generatePostSubCategoryLandingPageUrl(PostSubcategory.Investment),
  },
  [LandingPageTypes.InteriorInspirationPage]: {
    label: viewMoreLinkLabel.decorInteriorInspirationPage,
    link: generatePostSubCategoryLandingPageUrl(
      PostSubcategory.InteriorInspiration,
    ),
  },
  [LandingPageTypes.RenovationPage]: {
    label: viewMoreLinkLabel.decorRenovationPage,
    link: generatePostSubCategoryLandingPageUrl(PostSubcategory.Renovation),
  },
  [LandingPageTypes.MaintenancePage]: {
    label: viewMoreLinkLabel.decorMaintenancePage,
    link: generatePostSubCategoryLandingPageUrl(PostSubcategory.Maintenance),
  },
};

const ViewMoreLink: React.FC<ViewMoreLinkProps> = ({
  landingPageType,
  onClick,
}) => {
  const viewMoreInfo = viewMoreLinkConfig[landingPageType];
  if (!viewMoreInfo) {
    return null;
  }

  return (
    <ViewMore
      href={viewMoreInfo.link}
      onClick={() => onClick?.(landingPageType)}
    >
      <span className="label" data-testid={'label'}>
        {viewMoreInfo.label}
      </span>
      <ArrowRight size={7} />
    </ViewMore>
  );
};

export default ViewMoreLink;
